import type { GalaxyState } from "../../types/galaxy";
import { FACTION_COLORS } from "../../types/galaxy";

const SPECTRAL_COLORS: Record<string, string> = {
  O: "#9bb0ff", B: "#aabfff", A: "#cad7ff", F: "#f8f7ff",
  G: "#fff4e0", K: "#ffd2a1", M: "#ffcc6f",
};

interface Props {
  galaxyState: GalaxyState;
  selectedSystemId: string | null;
  onSetCourse: (systemId: string) => void;
}

export function SystemPanel({ galaxyState, selectedSystemId, onSetCourse }: Props) {
  if (!selectedSystemId) {
    return <ShipStatusView galaxyState={galaxyState} />;
  }

  const system = galaxyState.systems.find(s => s.id === selectedSystemId);
  if (!system) return null;

  const factionColor = FACTION_COLORS[system.faction ?? ""] ?? "#666";
  const starColor = SPECTRAL_COLORS[system.spectralClass] ?? "#fff";
  const isCurrentLocation = system.id === galaxyState.player.location;

  // Faction presence in this system
  const presences = galaxyState.factionPresence
    .filter(p => p.systemId === system.id && p.level !== "none");

  // Recent events in this system
  const events = galaxyState.recentEvents
    .filter(e => e.systemId === system.id && e.visibility === "public")
    .slice(0, 5);

  // Available missions in this system
  const missions = galaxyState.availableMissions
    .filter(m => m.systemId === system.id);

  // Distance from current location
  const currentSys = galaxyState.systems.find(s => s.id === galaxyState.player.location);
  const distance = currentSys ? Math.sqrt(
    (system.position.x - currentSys.position.x) ** 2 +
    (system.position.y - currentSys.position.y) ** 2
  ) : 0;
  const travelHours = Math.max(0.5, distance * 2);

  return (
    <div className="system-panel">
      {/* Header */}
      <div className="sp-header">
        <div className="sp-name-row">
          <span className="sp-star-dot" style={{ background: starColor }} />
          <span className="sp-system-name">{system.name}</span>
          <span className="sp-spectral">({system.spectralClass})</span>
        </div>
        <div className="sp-faction" style={{ color: factionColor }}>
          {system.faction ?? "Unaffiliated"}
        </div>
        <div className="sp-region">{system.region}</div>
      </div>

      {/* Description */}
      <div className="sp-section">
        <div className="sp-description">{system.description}</div>
      </div>

      {/* Summary */}
      <div className="sp-section">
        <div className="sp-section-title">SYSTEM DATA</div>
        <div className="sp-stat-row">
          <span className="sp-stat-label">Bodies</span>
          <span className="sp-stat-value">{system.bodies}</span>
        </div>
        <div className="sp-stat-row">
          <span className="sp-stat-label">Stations</span>
          <span className="sp-stat-value">{system.stations}</span>
        </div>
        {system.notableFeatures.length > 0 && (
          <div className="sp-stat-row">
            <span className="sp-stat-label">Features</span>
            <span className="sp-stat-value sp-features">{system.notableFeatures.join(", ")}</span>
          </div>
        )}
      </div>

      {/* Faction Presence */}
      {presences.length > 0 && (
        <div className="sp-section">
          <div className="sp-section-title">FACTION PRESENCE</div>
          {presences.map(p => (
            <div key={p.factionId} className="sp-presence-row">
              <span
                className="sp-presence-dot"
                style={{ background: FACTION_COLORS[p.factionId] ?? "#666" }}
              />
              <span className="sp-presence-name">{p.factionId}</span>
              <span className={`sp-presence-level sp-level-${p.level}`}>
                {p.level}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Events */}
      {events.length > 0 && (
        <div className="sp-section">
          <div className="sp-section-title">RECENT EVENTS</div>
          {events.map(e => (
            <div key={e.id} className="sp-event">
              <div className="sp-event-time">{formatRelativeTime(e.timestamp)}</div>
              <div className="sp-event-headline">{e.headline}</div>
            </div>
          ))}
        </div>
      )}

      {/* Available Missions */}
      {missions.length > 0 && (
        <div className="sp-section">
          <div className="sp-section-title">AVAILABLE MISSIONS</div>
          {missions.map(m => (
            <div key={m.id} className="sp-mission">
              <div className="sp-mission-header">
                <span className={`sp-mission-urgency sp-urgency-${m.urgency}`}>
                  {m.urgency}
                </span>
                <span className="sp-mission-type">{m.type}</span>
              </div>
              <div className="sp-mission-title">{m.title}</div>
              <div className="sp-mission-desc">{m.description}</div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation */}
      {!isCurrentLocation && (
        <div className="sp-section sp-nav-section">
          <div className="sp-stat-row">
            <span className="sp-stat-label">Distance</span>
            <span className="sp-stat-value">{distance.toFixed(1)} ly</span>
          </div>
          <div className="sp-stat-row">
            <span className="sp-stat-label">Est. travel</span>
            <span className="sp-stat-value">{travelHours.toFixed(1)} hours</span>
          </div>
          <button
            className="sp-set-course-btn"
            onClick={() => onSetCourse(system.id)}
          >
            SET COURSE
          </button>
        </div>
      )}

      {isCurrentLocation && (
        <div className="sp-section sp-nav-section">
          <div className="sp-current-badge">CURRENT LOCATION</div>
          {missions.length > 0 && (
            <button
              className="sp-set-course-btn"
              onClick={() => onSetCourse(system.id)}
            >
              LAUNCH MISSION
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ship Status (shown when no system selected) ──

function ShipStatusView({ galaxyState }: { galaxyState: GalaxyState }) {
  const { player } = galaxyState;
  const currentSystem = galaxyState.systems.find(s => s.id === player.location);

  return (
    <div className="system-panel">
      <div className="sp-header">
        <div className="sp-system-name">{player.shipName}</div>
        <div className="sp-region">{player.shipClass}</div>
        <div className="sp-faction" style={{ color: FACTION_COLORS[player.faction] ?? "#4a8eff" }}>
          {player.faction}
        </div>
      </div>

      {/* Location */}
      <div className="sp-section">
        <div className="sp-section-title">LOCATION</div>
        <div className="sp-stat-value" style={{ fontSize: 12 }}>
          {currentSystem?.name ?? player.location}
        </div>
        {currentSystem && (
          <div className="sp-region">{currentSystem.region}</div>
        )}
      </div>

      {/* Ship condition */}
      <div className="sp-section">
        <div className="sp-section-title">SHIP STATUS</div>
        <div className="sp-stat-row">
          <span className="sp-stat-label">Hull</span>
          <span className={`sp-stat-value sp-hull-${player.hullCondition}`}>
            {player.hullCondition}
          </span>
        </div>
        <div className="sp-stat-row">
          <span className="sp-stat-label">Shields</span>
          <span className="sp-stat-value">{player.shieldsStatus}</span>
        </div>
        {Object.entries(player.systems).map(([key, val]) => (
          <div key={key} className="sp-stat-row">
            <span className="sp-stat-label">{key}</span>
            <span className={`sp-stat-value ${val === "operational" ? "sp-sys-ok" : "sp-sys-warn"}`}>
              {val}
            </span>
          </div>
        ))}
      </div>

      {/* Crew */}
      <div className="sp-section">
        <div className="sp-section-title">BRIDGE CREW</div>
        {player.crew.map(c => (
          <div key={c.name} className="sp-crew-row">
            <span className="sp-crew-name">{c.name}</span>
            <span className="sp-crew-role">{c.role}</span>
            <span className={`sp-crew-status sp-crew-${c.status}`}>{c.status}</span>
          </div>
        ))}
      </div>

      {/* Faction standing */}
      <div className="sp-section">
        <div className="sp-section-title">FACTION STANDING</div>
        {Object.entries(player.factionStanding).map(([faction, standing]) => (
          <div key={faction} className="sp-standing-row">
            <span
              className="sp-standing-dot"
              style={{ background: FACTION_COLORS[faction] ?? "#666" }}
            />
            <span className="sp-standing-name">{faction}</span>
            <div className="sp-standing-bar">
              <div
                className="sp-standing-fill"
                style={{
                  width: `${Math.abs(standing) / 2}%`,
                  marginLeft: standing < 0 ? `${50 - Math.abs(standing) / 2}%` : "50%",
                  background: standing >= 0
                    ? `rgba(45, 212, 191, ${0.3 + Math.abs(standing) / 200})`
                    : `rgba(239, 68, 68, ${0.3 + Math.abs(standing) / 200})`,
                }}
              />
              <div className="sp-standing-center" />
            </div>
            <span className="sp-standing-value">{standing > 0 ? `+${standing}` : standing}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ──

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
